import { IOptions } from '@/data/models/option';
import React, { useEffect, useRef, useState } from 'react';
import Badge from './Badge';
import styles from './MultiSelectDropdown.module.scss';

interface Props {
  options: IOptions[];
  selectedOptions: IOptions[];
  onChange: (options: IOptions[]) => void;
}

export default function MultiSelectDropdown({
  options,
  selectedOptions,
  onChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: IOptions) => {
    if (option.value === 'selectAll') {
      const allOptions = options.filter((o) => o.value !== 'selectAll');
      if (selectedOptions.length === allOptions.length) {
        onChange([]);
      } else {
        onChange(allOptions);
      }
      setIsOpen(false);
      return;
    }

    if (!selectedOptions.find((o) => o.value === option.value)) {
      onChange([...selectedOptions, option]);
    }
    setIsOpen(false);
  };

  const handleRemove = (option: IOptions) => {
    onChange(selectedOptions.filter((o) => o.value !== option.value));
  };

  const allRegularOptions = options.filter((o) => o.value !== 'selectAll');
  const isAllSelected = selectedOptions.length === allRegularOptions.length;
  const availableOptions = options.filter((option) => {
    if (option.value === 'selectAll') return !isAllSelected;
    return !selectedOptions.some((selected) => selected.value === option.value);
  });

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={`${styles.inputContainer} ${
          isOpen ? styles.inputContainerOpen : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.badges}>
          {selectedOptions.length === 0 && (
            <span className={styles.placeholder}>Select</span>
          )}
          {selectedOptions.map((option) => (
            <Badge
              key={option.value}
              label={option.label}
              onRemove={() => handleRemove(option)}
            />
          ))}
        </div>
        <div className={styles.indicatorContainer}>
          <i className={`fa-solid fa-angle-down ${styles.icon}`}></i>
        </div>
      </div>
      {isOpen && availableOptions.length > 0 && (
        <ul className={styles.dropdownList}>
          {availableOptions.map((option) => (
            <li
              key={option.value}
              className={styles.dropdownItem}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
